import React, { useState, useEffect } from 'react';
import { Shield, Lock, Key, Smartphone, Clock, AlertTriangle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import api from '../../api';

const SecurityManagement = ({ userId }) => {
  // Change Password State
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Two-Factor Auth State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);

  // Login Activity State
  const [loginActivity, setLoginActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    fetchTwoFactorStatus();
    fetchLoginActivity();
  }, [userId]);

  const fetchTwoFactorStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/user/security/2fa/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFactorEnabled(response.data.enabled || false);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchLoginActivity = async () => {
    try {
      setActivityLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/user/security/login-activity', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginActivity(response.data || []);
    } catch (error) {
      // Mock data for demonstration
      setLoginActivity([
        {
          id: 1,
          timestamp: new Date().toISOString(),
          ipAddress: '192.168.1.1',
          device: 'Chrome on Windows',
          location: 'Mumbai, India',
          status: 'Success'
        },
        {
          id: 2,
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          ipAddress: '192.168.1.2',
          device: 'Safari on iPhone',
          location: 'Delhi, India',
          status: 'Success'
        }
      ]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setChangingPassword(true);
      const token = localStorage.getItem('token');
      await api.post('/user/security/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password. Please check your current password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setTwoFactorLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/user/security/2fa/enable', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.qrCode) {
        setQrCode(response.data.qrCode);
        setShowTwoFactorSetup(true);
      }
    } catch (error) {
      alert('Failed to enable two-factor authentication. This feature is coming soon.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    try {
      setTwoFactorLoading(true);
      const token = localStorage.getItem('token');
      await api.post('/user/security/2fa/verify', {
        code: verificationCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTwoFactorEnabled(true);
      setShowTwoFactorSetup(false);
      setVerificationCode('');
      alert('Two-factor authentication enabled successfully!');
    } catch (error) {
      alert('Invalid verification code. Please try again.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) {
      return;
    }

    try {
      setTwoFactorLoading(true);
      const token = localStorage.getItem('token');
      await api.post('/user/security/2fa/disable', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTwoFactorEnabled(false);
      alert('Two-factor authentication disabled successfully!');
    } catch (error) {
      alert('Failed to disable two-factor authentication.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Shield className="w-8 h-8 text-blue-600" />
          Security Settings
        </h2>
        <p className="text-gray-600">Manage your account security and authentication settings</p>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            Change Password
          </h3>
        </div>
        <div className="p-6">
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password *
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password *
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Error/Success Messages */}
            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{passwordError}</span>
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>{passwordSuccess}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={changingPassword}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>

      {/* Two-Factor Authentication Section - DISABLED */}
      {/* Temporarily disabled - can be re-enabled in the future */}
      {/*
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            Two-Factor Authentication
          </h3>
        </div>
        <div className="p-6">
          <div className="max-w-2xl">
            <p className="text-gray-600 mb-4">
              Add an extra layer of security to your account. You'll need to enter a code from your authenticator app when signing in.
            </p>

            {twoFactorEnabled ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Two-Factor Authentication is Enabled</p>
                    <p className="text-sm text-green-700">Your account is protected with 2FA</p>
                  </div>
                </div>
                <button
                  onClick={handleDisable2FA}
                  disabled={twoFactorLoading}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:bg-red-400 disabled:cursor-not-allowed"
                >
                  {twoFactorLoading ? 'Processing...' : 'Disable 2FA'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {!showTwoFactorSetup ? (
                  <>
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-900">Two-Factor Authentication is Disabled</p>
                        <p className="text-sm text-yellow-700">Enable 2FA to secure your account</p>
                      </div>
                    </div>
                    <button
                      onClick={handleEnable2FA}
                      disabled={twoFactorLoading}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-green-400 disabled:cursor-not-allowed"
                    >
                      {twoFactorLoading ? 'Processing...' : 'Enable 2FA'}
                    </button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Setup Two-Factor Authentication</h4>
                      <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                        <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                        <li>Scan the QR code below with your app</li>
                        <li>Enter the 6-digit code from your app</li>
                      </ol>
                    </div>
                    {qrCode && (
                      <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                        <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                      </div>
                    )}
                    <form onSubmit={handleVerify2FA} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Verification Code
                        </label>
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                          pattern="\d{6}"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowTwoFactorSetup(false)}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={twoFactorLoading || verificationCode.length !== 6}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-green-400 disabled:cursor-not-allowed"
                        >
                          {twoFactorLoading ? 'Verifying...' : 'Verify & Enable'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      */}

      {/* Login Activity Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600" />
            Login Activity
          </h3>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Recent login attempts and sessions on your account
          </p>

          {activityLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : loginActivity.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No login activity found
            </div>
          ) : (
            <div className="space-y-3">
              {loginActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <div className={`p-2 rounded-full ${activity.status === 'Success' ? 'bg-green-100' : 'bg-red-100'}`}>
                    {activity.status === 'Success' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{activity.device}</p>
                        <p className="text-sm text-gray-600">{activity.location}</p>
                        <p className="text-sm text-gray-500">IP: {activity.ipAddress}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{formatDate(activity.timestamp)}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          activity.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {activity.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 flex items-start gap-2">
              <Key className="w-4 h-4 mt-0.5" />
              <span>
                If you notice any suspicious activity, please change your password immediately and enable two-factor authentication.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityManagement;

